/**
 * §P.7 · Buscar la dirección y que el mapa vaya allí · 28-09-2026
 *
 * Se escribe «Calle Malagueñas, 17 Coria del Río 41111», se pulsa «Buscar» y el
 * marcador salta a ese sitio. **No guarda nada**: el punto se guarda al guardar la
 * sede, como si se hubiera movido a mano, porque lo ha elegido una persona mirando
 * el mapa.
 *
 * Viene relleno con la dirección de la sede, que es lo que se busca casi siempre.
 *
 * ## Lo que se dice después, según lo que encontró
 *
 * Con la calle pero sin el número —OpenStreetMap no tiene todos los portales— el
 * marcador cae en la calle, no en la puerta, y hay que decirlo: si no, el punto se
 * guarda en medio de la calzada y el buscador de familias manda a la gente ahí.
 */
import { useState } from 'react';
import { api } from '@/shared/api/client';
import { aProblema } from '@/shared/api/errores';

export interface PuntoEncontrado {
  readonly latitude: number;
  readonly longitude: number;
  readonly precision: 'exacta' | 'calle' | 'codigo_postal' | 'ciudad';
  readonly label: string;
}

/** Qué decir de lo encontrado. Exportado para probarlo sin montar nada. */
export function queDecir(p: PuntoEncontrado): string {
  switch (p.precision) {
    case 'exacta':
      return `Encontrado: ${p.label}. Comprueba que el marcador está en la entrada.`;
    case 'calle':
      return `Hemos encontrado la calle, pero no el número: ${p.label}. Arrastra el marcador hasta la puerta.`;
    case 'codigo_postal':
      return `Solo hemos encontrado la zona del código postal: ${p.label}. Acerca el mapa y pulsa donde está la sede.`;
    default:
      return `Solo hemos encontrado el pueblo: ${p.label}. Acerca el mapa y pulsa donde está la sede.`;
  }
}

export function BuscarEnElMapa({
  club,
  direccionInicial,
  alEncontrar,
}: {
  club: string;
  direccionInicial: string;
  alEncontrar: (punto: PuntoEncontrado) => void;
}) {
  const [texto, setTexto] = useState(direccionInicial);
  const [buscando, setBuscando] = useState(false);
  const [mensaje, setMensaje] = useState<{
    texto: string;
    malo: boolean;
  } | null>(null);

  const buscar = async () => {
    const q = texto.trim();
    if (q.length < 3) return;
    setBuscando(true);
    setMensaje(null);
    try {
      const { data, error } = await api.v1
        .clubs({ club })
        .venues.locate.get({ query: { q } });
      if (error) {
        setMensaje({
          texto: aProblema(error).detail ?? 'No se pudo buscar.',
          malo: true,
        });
        return;
      }
      const punto = data as unknown as PuntoEncontrado;
      alEncontrar(punto);
      setMensaje({
        texto: queDecir(punto),
        malo: punto.precision !== 'exacta',
      });
    } catch (e) {
      setMensaje({
        texto: aProblema(e).detail ?? 'No se pudo buscar.',
        malo: true,
      });
    } finally {
      setBuscando(false);
    }
  };

  return (
    <div className="buscar-en-mapa">
      {/*
        No es un <form>: está dentro del formulario de la sede, y un formulario
        dentro de otro no es HTML válido. Intro busca, igual que el botón.
      */}
      <label className="buscar-en-mapa-campo">
        <span>Buscar la dirección en el mapa</span>
        <input
          type="search"
          value={texto}
          maxLength={200}
          placeholder="Calle Malagueñas, 17 Coria del Río 41111"
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void buscar();
            }
          }}
        />
      </label>
      <button
        type="button"
        className="btn-plano"
        disabled={buscando || texto.trim().length < 3}
        onClick={() => void buscar()}
      >
        {buscando ? 'Buscando…' : 'Buscar'}
      </button>
      {mensaje && (
        <p
          className={mensaje.malo ? 'buscar-en-mapa-aviso' : 'tenue'}
          role="status"
        >
          {mensaje.texto}
        </p>
      )}
    </div>
  );
}
